// SCR-STAFF-935 — فهرست دستگاه‌ها (US-ORG-030، 035): فیلتر شعبه، وضعیت و نوع؛ صفحه‌بندی Cursor؛ ثبت دستگاه با فیلدهای پایه (همه پلن‌ها —
// AC-ORG-030.1) و با feature.org.devices فیلدهای پیشرفته و شمارنده (AC-ORG-030.2)؛ سریال تکراری روی فیلد (PHX-ORG-0301).
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type DeviceStatus, type DeviceView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  EmptyState,
  faDigits,
  FormDialog,
  JalaliDatePickerRHF,
  ListPage,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { BranchFilter, ORG_CODE_PATTERN, orgKeys, useBranchSelection } from './common';
import { COUNTER_UNITS, DEVICE_STATUSES, DEVICES_FEATURE, deviceKeys, deviceTone, useDeviceTypes } from './deviceCommon';

export const SCR_STAFF_935 = 'SCR-STAFF-935';
export const DEVICES_PATH = '/settings/devices';

export function DevicesPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.device.manage');
  const branches = useBranchSelection();
  const types = useDeviceTypes();
  const [status, setStatus] = useState<DeviceStatus | ''>('');
  const [typeId, setTypeId] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  const devices = useQuery({
    queryKey: [...deviceKeys.devices(session?.tenantId), branches.branchId, status, typeId, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/devices', {
          params: { query: { branchId: branches.branchId, status: status || undefined, deviceTypeId: typeId || undefined, cursor, limit: 50 } },
        }),
      ),
    enabled: Boolean(branches.branchId),
  });

  const columns: DataTableColumn<DeviceView>[] = [
    {
      id: 'name',
      headerKey: 'org:devices.columns.name',
      cell: (row) => (
        <Link component={RouterLink} to={`${DEVICES_PATH}/${row.id}`} data-testid={`device-link-${row.code}`}>
          {row.name}
        </Link>
      ),
    },
    { id: 'code', headerKey: 'org:devices.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'type', headerKey: 'org:devices.columns.type', cell: (row) => row.deviceTypeName ?? row.deviceTypeCode },
    { id: 'room', headerKey: 'org:devices.columns.room', cell: (row) => row.roomName ?? t('common:states.none') },
    {
      id: 'counter',
      headerKey: 'org:devices.columns.counter',
      cell: (row) =>
        row.lastReading === null || row.lastReading === undefined
          ? t('common:states.none')
          : `${faDigits(Number(row.lastReading))} ${t(`org:counter_unit.${row.counterUnit}`)}`,
    },
    {
      id: 'status',
      headerKey: 'org:devices.columns.status',
      cell: (row) => (
        <span data-testid={`device-status-${row.code}`} data-status={row.status}>
          <StatusChip tone={deviceTone(row.status)} labelKey={`org:device_status.${row.status}`} />
        </span>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_935}
      titleKey="org:devices.title"
      subtitleKey="org:devices.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!write.canWrite || !branches.branchId} data-testid="device-create">
          {t('org:devices.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <BranchFilter options={branches.options} value={branches.branchId} onChange={(value) => { setCursor(undefined); branches.setBranchId(value); }} />
            <TextField select size="small" label={t('org:devices.filters.status')} value={status} onChange={(event) => { setCursor(undefined); setStatus(event.target.value as DeviceStatus | ''); }} sx={{ minWidth: 180 }} slotProps={{ htmlInput: { 'data-testid': 'device-filter-status' } }}>
              <MenuItem value="">{t('org:rooms.filters.all')}</MenuItem>
              {DEVICE_STATUSES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`org:device_status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label={t('org:devices.filters.type')} value={typeId} onChange={(event) => { setCursor(undefined); setTypeId(event.target.value); }} sx={{ minWidth: 200 }} slotProps={{ htmlInput: { 'data-testid': 'device-filter-type' } }}>
              <MenuItem value="">{t('org:rooms.filters.all')}</MenuItem>
              {(types.data?.items ?? []).map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <WriteNotice write={write} />
        </Stack>
      }
    >
      {branches.options.length === 0 ? (
        <EmptyState titleKey="org:no_branch" />
      ) : (
        <>
          <DataTable
            captionKey="org:devices.caption"
            columns={columns}
            rows={devices.data?.items}
            getRowId={(row) => row.id}
            isLoading={devices.isPending}
            error={devices.error}
            onRetry={() => void devices.refetch()}
            emptyTitleKey="org:devices.empty"
          />
          {devices.data?.nextCursor && (
            <Button onClick={() => setCursor(devices.data?.nextCursor ?? undefined)} data-testid="device-next">
              {t('org:devices.next')}
            </Button>
          )}
        </>
      )}
      {creating && branches.branchId && <DeviceFormDialog branchId={branches.branchId} device={null} onClose={() => setCreating(false)} />}
    </ListPage>
  );
}

const deviceSchema = z.object({
  code: z.string(),
  name: zRequiredText(200),
  deviceTypeId: z.string().min(1, { error: 'common:validation.required' }),
  roomId: z.string(),
  manufacturer: zRequiredText(100),
  model: zRequiredText(100),
  serialNumber: zRequiredText(100),
  isBookable: z.boolean(),
  counterUnit: z.string(),
  initialCounter: z.string().regex(/^\d*$/, { error: 'common:validation.invalid' }),
  purchaseDate: z.string().nullable(),
  purchasePrice: z.number().int().nonnegative().nullable(),
  supplierName: z.string().max(100, { error: 'common:validation.too_long' }),
  assetNumber: z.string().max(100, { error: 'common:validation.too_long' }),
});
type DeviceForm = z.input<typeof deviceSchema>;

/** فرم ثبت یا ویرایش دستگاه (مکان فقط با جابه‌جایی تغییر می‌کند). */
export function DeviceFormDialog({ branchId, device, onClose }: { branchId: string; device: DeviceView | null; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const advanced = access.featureEnabled(DEVICES_FEATURE);
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const types = useDeviceTypes();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = device === null;
  const rooms = useQuery({
    queryKey: [...orgKeys.rooms(session?.tenantId), branchId, 'device-form'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/rooms', { params: { query: { branchId, status: 'Active' } } })),
    enabled: creating,
  });
  const { control, handleSubmit, formState, setError, watch } = useForm<DeviceForm>({
    resolver: zodResolver(
      deviceSchema.superRefine((value, ctx) => {
        if (creating && !ORG_CODE_PATTERN.test(value.code.trim())) {
          ctx.addIssue({ code: 'custom', path: ['code'], message: 'errors:validation.org_code' });
        }
      }),
    ),
    defaultValues: {
      code: device?.code ?? '',
      name: device?.name ?? '',
      deviceTypeId: device?.deviceTypeId ?? '',
      roomId: device?.roomId ?? '',
      manufacturer: device?.manufacturer ?? '',
      model: device?.model ?? '',
      serialNumber: device?.serialNumber ?? '',
      isBookable: device?.isBookable ?? true,
      counterUnit: device?.counterUnit ?? '',
      initialCounter: '',
      purchaseDate: device?.purchaseDate ?? null,
      purchasePrice: device?.purchasePrice ? Number(device.purchasePrice.amount) : null,
      supplierName: device?.supplierName ?? '',
      assetNumber: device?.assetNumber ?? '',
    },
  });
  const selectedType = types.data?.items.find((type) => type.id === watch('deviceTypeId'));

  const save = useMutation({
    mutationFn: (form: DeviceForm) => {
      const body = {
        branchId: null,
        roomId: null,
        code: null,
        counterUnit: null,
        initialCounter: null,
        rowVersion: null,
        deviceTypeId: form.deviceTypeId,
        name: form.name,
        manufacturer: form.manufacturer,
        model: form.model,
        serialNumber: form.serialNumber,
        isBookable: form.isBookable,
        purchaseDate: advanced ? form.purchaseDate : null,
        purchasePrice: advanced && form.purchasePrice !== null && form.purchasePrice !== undefined ? { amount: form.purchasePrice, currency: 'IRR' } : null,
        supplierName: advanced ? form.supplierName.trim() || null : null,
        assetNumber: advanced ? form.assetNumber.trim() || null : null,
      };
      return creating
        ? unwrap(
            tenantApi.POST('/api/v1/organization/devices', {
              body: {
                ...body,
                branchId,
                roomId: form.roomId || null,
                code: form.code.trim(),
                counterUnit: advanced && form.counterUnit ? (form.counterUnit as DeviceView['counterUnit']) : null,
                initialCounter: advanced && form.initialCounter ? Number(form.initialCounter) : null,
              },
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/organization/devices/{id}', {
              params: { path: { id: device.id } },
              body: { ...body, rowVersion: device.rowVersion },
            }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.devices(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'deviceTypeId', 'roomId', 'manufacturer', 'model', 'serialNumber'],
        aliases: { device_type_id: 'deviceTypeId', room_id: 'roomId', serial_number: 'serialNumber' },
        codeFields: { 'PHX-ORG-0301': 'serialNumber', 'PHX-ORG-0302': 'code', 'PHX-ORG-0305': 'roomId', 'PHX-ORG-0309': 'deviceTypeId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-935-FORM"
      titleKey={creating ? 'org:devices.create_title' : 'org:devices.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
      maxWidth="md"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="name" labelKey="org:devices.form.name" required fullWidth autoFocus slotProps={{ htmlInput: { 'data-testid': 'device-name' } }} />
        <TextFieldRHF control={control} name="code" labelKey="org:devices.form.code" required fullWidth disabled={!creating} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-code' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="deviceTypeId"
          labelKey="org:devices.form.type"
          required
          fullWidth
          options={(types.data?.items ?? []).filter((type) => type.isActive || type.id === device?.deviceTypeId).map((type) => ({ value: type.id, label: type.name }))}
        />
        {creating && (
          <SelectRHF
            control={control}
            name="roomId"
            labelKey="org:devices.form.room"
            fullWidth
            options={[{ value: '', labelKey: 'org:devices.form.room_none' }, ...(rooms.data?.items ?? []).map((room) => ({ value: room.id, label: room.name }))]}
          />
        )}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="manufacturer" labelKey="org:devices.form.manufacturer" required fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-manufacturer' } }} />
        <TextFieldRHF control={control} name="model" labelKey="org:devices.form.model" required fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-model' } }} />
        <TextFieldRHF control={control} name="serialNumber" labelKey="org:devices.form.serial" required fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-serial' } }} />
      </Stack>
      <CheckboxRHF control={control} name="isBookable" labelKey="org:devices.form.is_bookable" />
      {advanced ? (
        <>
          {creating && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <SelectRHF
                control={control}
                name="counterUnit"
                labelKey="org:devices.form.counter_unit"
                fullWidth
                options={[
                  { value: '', label: selectedType ? t('org:devices.form.counter_unit_default', { unit: t(`org:counter_unit.${selectedType.counterUnit}`) }) : t('org:devices.form.counter_unit_type') },
                  ...COUNTER_UNITS.map((value) => ({ value, labelKey: `org:counter_unit.${value}` })),
                ]}
              />
              <TextFieldRHF control={control} name="initialCounter" labelKey="org:devices.form.initial_counter" fullWidth slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'device-initial-counter' } }} />
            </Stack>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <JalaliDatePickerRHF control={control} name="purchaseDate" labelKey="org:devices.form.purchase_date" />
            <MoneyFieldRHF control={control} name="purchasePrice" labelKey="org:devices.form.purchase_price" />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextFieldRHF control={control} name="supplierName" labelKey="org:devices.form.supplier" fullWidth />
            <TextFieldRHF control={control} name="assetNumber" labelKey="org:devices.form.asset_number" fullWidth slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'device-asset-number' } }} />
          </Stack>
        </>
      ) : (
        <StatusChip tone="pending" labelKey="org:devices.form.advanced_locked" />
      )}
    </FormDialog>
  );
}
